import { useState, useMemo, useEffect } from 'react';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useAssetsStore } from '@/stores/assetsStore';
import { useRisks, useTasks, useEvidence } from '@/hooks/queries';
import { useSharedInfrastructureStore } from '@/stores/sharedInfrastructureStore';
import { useAuth } from '@/hooks/useAuth';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Eye,
  Pencil,
  Trash2,
  ShieldAlert,
  Building2,
  Network,
  Server,
  HardDrive,
  Search,
  AlertTriangle,
  CheckCircle2,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { InspectorDrawer } from './InspectorDrawer';
import { SiteFormDialog } from '../sites/SiteFormDialog';
import { ZoneFormDialog } from '../sites/ZoneFormDialog';
import { SystemFormDialog } from '../systems/SystemFormDialog';
import type { ZoneInsert, ZoneUpdate } from '@/types/zone';

export function AssetsTreeView() {
  const { sites, loadSites, deleteSite, addSite, updateSite } = useSiteStore();
  const { zones, loadZones, deleteZone, addZone, updateZone } = useZoneStore();
  const { systems, loadSystems, deleteSystem } = useSystemStore();
  const { nodes: assets, loadAssets, deleteNode, setSelectedNode } = useAssetsStore();
  const { organization } = useAuth();
  const { risks } = useRisks(organization?.id);
  const { tasks } = useTasks(organization?.id);
  const { evidence } = useEvidence(organization?.id);
  const { infrastructures, loadInfrastructures } = useSharedInfrastructureStore();

  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<string | undefined>();
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState<string | undefined>();
  const [zoneSiteId, setZoneSiteId] = useState<string>('');
  const [systemDialogOpen, setSystemDialogOpen] = useState(false);
  const [editingSystemId, setEditingSystemId] = useState<string | undefined>();
  const [systemZoneId, setSystemZoneId] = useState<string>('');

  useEffect(() => {
    loadSites();
    loadZones();
    loadSystems();
    loadAssets();
    loadInfrastructures();
  }, []);

  useEffect(() => {
    if (!systemDialogOpen) {
      loadSystems();
    }
  }, [systemDialogOpen, loadSystems]);

  const toggleSite = (siteId: string) => {
    setExpandedSites(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const toggleZone = (zoneId: string) => {
    setExpandedZones(prev => {
      const next = new Set(prev);
      if (next.has(zoneId)) {
        next.delete(zoneId);
      } else {
        next.add(zoneId);
      }
      return next;
    });
  };

  const toggleSystem = (systemId: string) => {
    setExpandedSystems(prev => {
      const next = new Set(prev);
      if (next.has(systemId)) {
        next.delete(systemId);
      } else {
        next.add(systemId);
      }
      return next;
    });
  };

  const getSiteCounts = (siteId: string) => {
    const siteZones = zones.filter(z => z.site_id === siteId);
    const zoneSharedInfra = infrastructures.filter(i => siteZones.some(z => z.id === i.zone_id));
    const zoneSystems = systems.filter(s => siteZones.some(z => z.id === s.zone_id));
    const systemAssets = assets.filter(a => zoneSystems.some(s => s.id === a.data.system_id));

    return {
      zones: siteZones.length,
      sharedInfra: zoneSharedInfra.length,
      systems: zoneSystems.length,
      endpoints: systemAssets.length,
      risks: (risks ?? []).filter(r => systemAssets.some(a => a.id === r.related_to_id) && r.status !== 'Closed').length,
      tasks: (tasks ?? []).filter(t => systemAssets.some(a => a.id === t.related_to_id) && t.status !== 'Done').length,
      evidence: (evidence ?? []).filter(e => systemAssets.some(a => a.id === e.related_to_id)).length,
    };
  };

  const getZoneCounts = (zoneId: string) => {
    const zoneSharedInfra = infrastructures.filter(i => i.zone_id === zoneId);
    const zoneSystems = systems.filter(s => s.zone_id === zoneId);
    const systemAssets = assets.filter(a => zoneSystems.some(s => s.id === a.data.system_id));

    return {
      sharedInfra: zoneSharedInfra.length,
      systems: zoneSystems.length,
      endpoints: systemAssets.length,
      risks: (risks ?? []).filter(r => systemAssets.some(a => a.id === r.related_to_id) && r.status !== 'Closed').length,
      tasks: (tasks ?? []).filter(t => systemAssets.some(a => a.id === t.related_to_id) && t.status !== 'Done').length,
      evidence: (evidence ?? []).filter(e => systemAssets.some(a => a.id === e.related_to_id)).length,
    };
  };

  const getSystemCounts = (systemId: string) => {
    const systemAssets = assets.filter(a => a.data.system_id === systemId);

    return {
      endpoints: systemAssets.length,
      risks: (risks ?? []).filter(r => systemAssets.some(a => a.id === r.related_to_id) && r.status !== 'Closed').length,
      tasks: (tasks ?? []).filter(t => systemAssets.some(a => a.id === t.related_to_id) && t.status !== 'Done').length,
      evidence: (evidence ?? []).filter(e => systemAssets.some(a => a.id === e.related_to_id)).length,
    };
  };

  const getAssetCounts = (assetId: string) => {
    return {
      risks: (risks ?? []).filter(r => r.related_to_id === assetId && r.status !== 'Closed').length,
      tasks: (tasks ?? []).filter(t => t.related_to_id === assetId && t.status !== 'Done').length,
      evidence: (evidence ?? []).filter(e => e.related_to_id === assetId).length,
    };
  };

  const filteredSites = useMemo(() => {
    if (!searchQuery) return sites;
    
    const query = searchQuery.toLowerCase();
    return sites.filter(site => {
      if (site.name.toLowerCase().includes(query)) return true;
      
      const siteZones = zones.filter(z => z.site_id === site.id);
      if (siteZones.some(z => z.name.toLowerCase().includes(query))) {
        setExpandedSites(prev => new Set([...prev, site.id]));
        return true;
      }
      
      const zoneSystems = systems.filter(s => siteZones.some(z => z.id === s.zone_id));
      if (zoneSystems.some(s => s.name.toLowerCase().includes(query))) {
        setExpandedSites(prev => new Set([...prev, site.id]));
        return true;
      }
      
      const systemAssets = assets.filter(a => zoneSystems.some(s => s.id === a.data.system_id));
      if (systemAssets.some(a => a.data.label.toLowerCase().includes(query))) {
        setExpandedSites(prev => new Set([...prev, site.id]));
        return true;
      }
      
      return false;
    });
  }, [sites, zones, systems, assets, searchQuery]);

  const sortedSites = useMemo(() => {
    return [...filteredSites].sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredSites]);

  const getSortedZones = (siteId: string) => {
    const siteZones = zones.filter(z => z.site_id === siteId);
    const domainOrder: Record<string, number> = { IT: 0, OT: 1, DMZ: 2, Cloud: 3, External: 4, IoT: 5, Physical: 6 };
    return siteZones.sort((a, b) => {
      const orderDiff = (domainOrder[a.domain] ?? 99) - (domainOrder[b.domain] ?? 99);
      if (orderDiff !== 0) return orderDiff;
      return a.name.localeCompare(b.name);
    });
  };

  const getSortedSystems = (zoneId: string) => {
    return systems.filter(s => s.zone_id === zoneId).sort((a, b) => a.name.localeCompare(b.name));
  };

  const getSortedAssets = (systemId: string) => {
    return assets.filter(a => a.data.system_id === systemId).sort((a, b) => 
      a.data.label.localeCompare(b.data.label)
    );
  };

  const handleViewDetails = (id: string, type: 'site' | 'zone' | 'system' | 'asset') => {
    if (type === 'asset') {
      setSelectedNode(id);
    }
  };

  const handleDelete = async (id: string, type: 'site' | 'zone' | 'system' | 'asset', name: string) => {
    if (!confirm(`Are you sure you want to delete ${name}?`)) return;

    try {
      if (type === 'site') {
        await deleteSite(id);
      } else if (type === 'zone') {
        await deleteZone(id);
      } else if (type === 'system') {
        await deleteSystem(id);
      } else if (type === 'asset') {
        await deleteNode(id);
      }
      toast.success(`${name} deleted successfully`);
    } catch (error) {
      toast.error(`Failed to delete ${name}`);
      console.error(error);
    }
  };

  const handleAddSystem = (zoneId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSystemId(undefined);
    setSystemZoneId(zoneId);
    setSystemDialogOpen(true);
  };

  const getCriticalityColor = (criticality?: string) => {
    if (!criticality) return '';
    const lower = criticality.toLowerCase();
    if (lower === 'high' || lower === 'h') return 'bg-destructive text-destructive-foreground';
    if (lower === 'medium' || lower === 'm') return 'bg-warning text-warning-foreground';
    if (lower === 'low' || lower === 'l') return 'bg-success text-success-foreground';
    return '';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sites, zones, systems, or assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={() => { setEditingSiteId(undefined); setSiteDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Asset
        </Button>
      </div>

      <div className="flex-1 border rounded-lg overflow-auto">
        <TooltipProvider>
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-[40%]">Name / Hierarchy</TableHead>
                <TableHead className="w-[12%]">Type</TableHead>
                <TableHead className="w-[12%]">Domain/Level</TableHead>
                <TableHead className="w-[10%]">Criticality</TableHead>
                <TableHead className="w-[14%]">Status</TableHead>
                <TableHead className="w-[12%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No sites found. Create your first site to get started.
                  </TableCell>
                </TableRow>
              ) : (
              sortedSites.map(site => {
                const siteIsExpanded = expandedSites.has(site.id);
                const counts = getSiteCounts(site.id);
                const siteZones = getSortedZones(site.id);

                return (
                  <>
                    <TableRow key={site.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 pl-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleSite(site.id)}
                          >
                            {siteIsExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className={cn("h-4 w-4", counts.zones === 0 && "text-muted-foreground/50")} />
                            )}
                          </Button>
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span>{site.name}</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "ml-2 text-xs cursor-pointer hover:bg-accent",
                                  counts.zones === 0 && counts.sharedInfra === 0 && "text-muted-foreground",
                                  (counts.zones > 0 || counts.sharedInfra > 0) && "text-green-700 bg-green-50 border-green-200"
                                )}
                                onClick={() => { setZoneSiteId(site.id); setEditingZoneId(undefined); setZoneDialogOpen(true); }}
                              >
                                {counts.zones}Z / {counts.sharedInfra}I / {counts.systems}S / {counts.endpoints}E
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {counts.zones} Zones, {counts.sharedInfra} Shared Infra, {counts.systems} Systems, {counts.endpoints} Endpoints
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary">Site</Badge></TableCell>
                      <TableCell>
                        {site.site_code ? (
                          <code className="text-xs bg-muted px-2 py-1 rounded">{site.site_code}</code>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {site.criticality && (
                          <Badge className={getCriticalityColor(site.criticality)}>{site.criticality}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {counts.risks > 0 && (
                            <div className="flex items-center gap-1 text-destructive">
                              <AlertTriangle className="h-4 w-4" />
                              <span className="text-sm font-medium">{counts.risks}</span>
                            </div>
                          )}
                          {counts.tasks > 0 && (
                            <div className="flex items-center gap-1 text-warning">
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="text-sm font-medium">{counts.tasks}</span>
                            </div>
                          )}
                          {counts.evidence > 0 && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <FileText className="h-4 w-4" />
                              <span className="text-sm font-medium">{counts.evidence}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setZoneSiteId(site.id); setEditingZoneId(undefined); setZoneDialogOpen(true); }}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Add Zone</TooltipContent>
                          </Tooltip>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(site.id, 'site')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingSiteId(site.id); setSiteDialogOpen(true); }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(site.id, 'site', site.name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Empty state when site has no zones */}
                    {siteIsExpanded && siteZones.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4 text-sm text-muted-foreground italic bg-muted/30">
                          <div className="flex items-center justify-center gap-2">
                            No zones yet - 
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-primary"
                              onClick={() => { setZoneSiteId(site.id); setEditingZoneId(undefined); setZoneDialogOpen(true); }}
                            >
                              click + to add
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {siteIsExpanded && siteZones.map(zone => {
                      const zoneIsExpanded = expandedZones.has(zone.id);
                      const zoneCounts = getZoneCounts(zone.id);
                      const zoneSystems = getSortedSystems(zone.id);
                      const isDMZ = zone.domain === 'DMZ';

                      return (
                        <>
                          <TableRow 
                            key={zone.id} 
                            className={cn(
                              "hover:bg-muted/50",
                              isDMZ && "bg-orange-50/50 border-l-4 border-orange-500"
                            )}
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2 pl-8">
                                <div className="w-px h-6 bg-border absolute left-6" />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => toggleZone(zone.id)}
                                >
                                  {zoneIsExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className={cn("h-4 w-4", zoneCounts.systems === 0 && "text-muted-foreground/50")} />
                                  )}
                                </Button>
                                <Network className="h-4 w-4 text-muted-foreground" />
                                <span>{zone.name}</span>
                                {isDMZ && (
                                  <Badge variant="destructive" className="ml-2 text-xs">
                                    <ShieldAlert className="h-3 w-3 mr-1" />
                                    DMZ
                                  </Badge>
                                )}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge 
                                        variant="outline" 
                                        className={cn(
                                          "ml-2 text-xs cursor-pointer hover:bg-accent",
                                          zoneCounts.systems === 0 && zoneCounts.sharedInfra === 0 && "text-muted-foreground",
                                          (zoneCounts.systems > 0 || zoneCounts.sharedInfra > 0) && "text-green-700 bg-green-50 border-green-200"
                                        )}
                                      >
                                        {zoneCounts.sharedInfra}I / {zoneCounts.systems}S / {zoneCounts.endpoints}E
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {zoneCounts.sharedInfra} Shared Infra, {zoneCounts.systems} Systems, {zoneCounts.endpoints} Endpoints
                                    </TooltipContent>
                                  </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell><Badge variant="secondary">Zone</Badge></TableCell>
                            <TableCell>
                              <Badge className={cn(
                                zone.domain === 'IT' && 'bg-blue-100 text-blue-700 border-blue-500',
                                zone.domain === 'OT' && 'bg-orange-100 text-orange-700 border-orange-500',
                                zone.domain === 'DMZ' && 'bg-yellow-100 text-yellow-700 border-yellow-500',
                                zone.domain === 'Cloud' && 'bg-purple-100 text-purple-700 border-purple-500'
                              )}>
                                {zone.domain}
                                {zone.purdue_level && ` / ${zone.purdue_level}`}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {zone.criticality && (
                                <Badge className={getCriticalityColor(zone.criticality)}>{zone.criticality}</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {zoneCounts.risks > 0 && (
                                  <div className="flex items-center gap-1 text-destructive">
                                    <AlertTriangle className="h-4 w-4" />
                                    <span className="text-sm font-medium">{zoneCounts.risks}</span>
                                  </div>
                                )}
                                {zoneCounts.tasks > 0 && (
                                  <div className="flex items-center gap-1 text-warning">
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span className="text-sm font-medium">{zoneCounts.tasks}</span>
                                  </div>
                                )}
                                {zoneCounts.evidence > 0 && (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <FileText className="h-4 w-4" />
                                    <span className="text-sm font-medium">{zoneCounts.evidence}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => handleAddSystem(zone.id, e)}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Add System</TooltipContent>
                                </Tooltip>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDetails(zone.id, 'zone')}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditingZoneId(zone.id); setZoneSiteId(site.id); setZoneDialogOpen(true); }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(zone.id, 'zone', zone.name)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>

                          {/* Empty state when zone has no systems */}
                          {zoneIsExpanded && zoneSystems.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-4 text-sm text-muted-foreground italic bg-muted/20">
                                <div className="flex items-center justify-center gap-2 pl-16">
                                  No systems yet - System creation coming soon
                                </div>
                              </TableCell>
                            </TableRow>
                          )}

                          {zoneIsExpanded && zoneSystems.map(system => {
                            const systemIsExpanded = expandedSystems.has(system.id);
                            const systemCounts = getSystemCounts(system.id);
                            const systemAssets = getSortedAssets(system.id);

                            return (
                              <>
                                <TableRow 
                                  key={system.id} 
                                  className={cn(
                                    "hover:bg-muted/50",
                                    isDMZ && "bg-orange-50/30"
                                  )}
                                >
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2 pl-16 relative">
                                      <div className="w-px h-6 bg-border absolute left-14" />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => toggleSystem(system.id)}
                                      >
                                        {systemIsExpanded ? (
                                          <ChevronDown className="h-4 w-4" />
                                        ) : (
                                          <ChevronRight className={cn("h-4 w-4", systemCounts.endpoints === 0 && "text-muted-foreground/50")} />
                                        )}
                                      </Button>
                                      <Server className="h-4 w-4 text-muted-foreground" />
                                      <span>{system.name}</span>
                                      <Badge 
                                        variant="outline" 
                                        className={cn(
                                          "ml-2 text-xs",
                                          systemCounts.endpoints === 0 && "text-muted-foreground",
                                          systemCounts.endpoints > 0 && "text-green-700 bg-green-50 border-green-200"
                                        )}
                                      >
                                        {systemCounts.endpoints}E
                                      </Badge>
                                    </div>
                                  </TableCell>
                                  <TableCell><Badge variant="secondary">System</Badge></TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className={cn(
                                      zone.domain === 'IT' && 'bg-blue-50 text-blue-700',
                                      zone.domain === 'OT' && 'bg-orange-50 text-orange-700',
                                      zone.domain === 'DMZ' && 'bg-yellow-50 text-yellow-700',
                                      zone.domain === 'Cloud' && 'bg-purple-50 text-purple-700'
                                    )}>
                                      {zone.domain}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-muted-foreground">-</span>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      {systemCounts.risks > 0 && (
                                        <div className="flex items-center gap-1 text-destructive">
                                          <AlertTriangle className="h-4 w-4" />
                                          <span className="text-sm font-medium">{systemCounts.risks}</span>
                                        </div>
                                      )}
                                      {systemCounts.tasks > 0 && (
                                        <div className="flex items-center gap-1 text-warning">
                                          <CheckCircle2 className="h-4 w-4" />
                                          <span className="text-sm font-medium">{systemCounts.tasks}</span>
                                        </div>
                                      )}
                                      {systemCounts.evidence > 0 && (
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                          <FileText className="h-4 w-4" />
                                          <span className="text-sm font-medium">{systemCounts.evidence}</span>
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled
                                          >
                                            <Plus className="h-4 w-4 text-muted-foreground" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Add Asset (coming soon)</TooltipContent>
                                      </Tooltip>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleViewDetails(system.id, 'system')}
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(system.id, 'system', system.name)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>

                                {/* Empty state when system has no assets */}
                                {systemIsExpanded && systemAssets.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={6} className="text-center py-4 text-sm text-muted-foreground italic bg-muted/10">
                                      <div className="flex items-center justify-center gap-2 pl-24">
                                        No assets yet - Asset creation coming soon
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {systemIsExpanded && systemAssets.map(asset => {
                                  const assetCounts = getAssetCounts(asset.id);

                                  return (
                                    <TableRow 
                                      key={asset.id} 
                                      className={cn(
                                        "hover:bg-muted/50",
                                        isDMZ && "bg-orange-50/20"
                                      )}
                                    >
                                      <TableCell className="font-medium">
                                        <div className="flex items-center gap-2 pl-24 relative">
                                          <div className="w-px h-6 bg-border absolute left-22" />
                                          <HardDrive className="h-4 w-4 text-muted-foreground" />
                                          <span>{asset.data.label}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="secondary">{asset.data.type || 'Asset'}</Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="outline" className={cn(
                                          zone.domain === 'IT' && 'bg-blue-50 text-blue-700',
                                          zone.domain === 'OT' && 'bg-orange-50 text-orange-700',
                                          zone.domain === 'DMZ' && 'bg-yellow-50 text-yellow-700',
                                          zone.domain === 'Cloud' && 'bg-purple-50 text-purple-700'
                                        )}>
                                          {zone.domain}
                                          {asset.data.purdue_level && ` / ${asset.data.purdue_level}`}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        {asset.data.criticality ? (
                                          <Badge className={getCriticalityColor(String(asset.data.criticality))}>
                                            {String(asset.data.criticality)}
                                          </Badge>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-3">
                                          {assetCounts.risks > 0 && (
                                            <div className="flex items-center gap-1 text-destructive">
                                              <AlertTriangle className="h-4 w-4" />
                                              <span className="text-sm font-medium">{assetCounts.risks}</span>
                                            </div>
                                          )}
                                          {assetCounts.tasks > 0 && (
                                            <div className="flex items-center gap-1 text-warning">
                                              <CheckCircle2 className="h-4 w-4" />
                                              <span className="text-sm font-medium">{assetCounts.tasks}</span>
                                            </div>
                                          )}
                                          {assetCounts.evidence > 0 && (
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                              <FileText className="h-4 w-4" />
                                              <span className="text-sm font-medium">{assetCounts.evidence}</span>
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleViewDetails(asset.id, 'asset')}
                                          >
                                            <Eye className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(asset.id, 'asset', asset.data.label)}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </>
                            );
                          })}
                        </>
                      );
                    })}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
        </TooltipProvider>
      </div>

      <div className="mt-4 text-sm text-muted-foreground">
        Showing {sortedSites.length} site{sortedSites.length !== 1 ? 's' : ''}
      </div>

      <InspectorDrawer open={false} onOpenChange={() => {}} />
      
      <SiteFormDialog 
        open={siteDialogOpen} 
        onOpenChange={setSiteDialogOpen}
        site={editingSiteId ? sites.find(s => s.id === editingSiteId) : null}
        onSubmit={async (data) => {
          try {
            if (editingSiteId) {
              await updateSite(editingSiteId, data);
              toast.success('Site updated successfully');
            } else {
              await addSite(data);
              toast.success('Site created successfully');
            }
            setSiteDialogOpen(false);
            setEditingSiteId(undefined);
          } catch (error) {
            toast.error('Failed to save site');
            console.error(error);
          }
        }}
      />
      
      <ZoneFormDialog 
        open={zoneDialogOpen} 
        onOpenChange={setZoneDialogOpen}
        zone={editingZoneId ? zones.find(z => z.id === editingZoneId) : undefined}
        siteId={zoneSiteId}
        onSubmit={async (data) => {
          try {
            if (editingZoneId) {
              await updateZone(editingZoneId, data as ZoneUpdate);
              toast.success('Zone updated successfully');
            } else {
              await addZone(data as ZoneInsert);
              toast.success('Zone created successfully');
            }
            setZoneDialogOpen(false);
            setEditingZoneId(undefined);
            setZoneSiteId('');
          } catch (error) {
            toast.error('Failed to save zone');
            console.error(error);
          }
        }}
      />

      <SystemFormDialog
        open={systemDialogOpen}
        onOpenChange={setSystemDialogOpen}
        system={editingSystemId ? systems.find(s => s.id === editingSystemId) : undefined}
        zoneId={systemZoneId}
      />
    </div>
  );
}
